<template>
  <div :id="id" class="location-card-detail flex">
    <div class="shipping-point-details">
      <h3 class="font-semibold section-subheader mt-0">
        {{
          t('common.paramDetails', {
            param: t('locations.locations'),
          })
        }}
      </h3>

      <div class="flex flex-column flex-gap-3">
        <PopupTextInput
          :id="`${id}-contact-company-name`"
          v-model="contact.contactCompanyName"
          :label="t('contacts.companyName')"
          :disabled="!editable"
        >
          <template #placeholder="{ label, placeholder }">
            <KeyValuePair :label="label" :value="placeholder">
              <template #label>
                <LabeledIcon icon="pi pi-building" :label="label" />
              </template>
            </KeyValuePair>
          </template>
        </PopupTextInput>

        <KeyValuePair
          v-if="isAuthenticatedUser"
          :label="t('locationGroups.locationGroups', 2)"
          :value="locationGroupNames || undefined"
        >
          <template #label>
            <LabeledIcon
              icon="pi pi-tag"
              :label="t('locationGroups.locationGroups', 2)"
            />
          </template>

          <template #value>
            <div class="location-groups-list">
              {{ locationGroupNames || '-' }}
            </div>
          </template>
        </KeyValuePair>

        <KeyValuePair
          :label="t('hoursOfOperation.hoursOfOperation')"
          :value="hoursOfOperationTooltip"
        >
          <template #label>
            <LabeledIcon
              icon="pi pi-clock"
              :label="t('hoursOfOperation.hoursOfOperation')"
            />
          </template>

          <template #value>
            <div v-if="isOnlyWeekdayHours" class="hours-of-operation">
              <span>
                {{ t('hoursOfOperation.Monday') }} -
                {{ t('hoursOfOperation.Friday') }}
              </span>
              <span>{{ formattedMondayTimes }}</span>
            </div>
            <div v-else-if="openHours.length" class="hours-of-operation">
              <div v-for="day of openHours" :key="day.dayOfWeek.name">
                <span>{{ day.dayOfWeek.abbrev }}</span>
                <span>
                  {{ formatTime(day.hours.from) }} -
                  {{ formatTime(day.hours.to) }}
                </span>
              </div>
            </div>
            <div v-else class="hours-of-operation">-</div>
          </template>
        </KeyValuePair>
      </div>
    </div>

    <div class="contact-details">
      <h3 class="font-semibold section-subheader mt-0">
        {{
          t('common.paramDetails', {
            param: t('contacts.contacts'),
          })
        }}
      </h3>

      <div class="flex flex-column flex-gap-3">
        <PopupTextInput
          :id="`${id}-contact-name`"
          v-model="contact.contactName"
          :label="t('common.name')"
          :disabled="!editable"
        >
          <template #placeholder="{ label, placeholder }">
            <KeyValuePair :label="label" :value="placeholder">
              <template #label>
                <LabeledIcon icon="pi pi-user" :label="label" />
              </template>
            </KeyValuePair>
          </template>
        </PopupTextInput>

        <PopupTextInput
          :id="`${id}-contact-phone-number`"
          v-model="contact.contactPhoneNumber"
          :label="t('common.phone')"
          :disabled="!editable"
        >
          <template #placeholder="{ label, placeholder }">
            <KeyValuePair :label="label" :value="placeholder">
              <template #label>
                <LabeledIcon icon="pi pi-phone" :label="label" />
              </template>
            </KeyValuePair>
          </template>
        </PopupTextInput>

        <PopupTextInput
          :id="`${id}-contact-email`"
          v-model="contact.contactEmail"
          :label="t('common.email')"
          :disabled="!editable"
        >
          <template #placeholder="{ label, placeholder }">
            <KeyValuePair :label="label" :value="placeholder">
              <template #label>
                <LabeledIcon icon="pi pi-envelope" :label="label" />
              </template>
            </KeyValuePair>
          </template>
        </PopupTextInput>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, PropType, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { HoursOfOperation } from '@rygen/rygen-ui/model';

import { EmbeddableContact } from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useLocationGroupStore } from '@/stores/LocationGroup';

import LabeledIcon from '@/components/ui/LabeledIcon.vue';
import PopupTextInput from '@rygen/rygen-ui/components/popup/PopupTextInput.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';

const DATE_STRING_TEMPLATE = '2024-06-26T$time.000';

const contact = defineModel('contact', {
  type: Object as PropType<EmbeddableContact>,
  default: () => new EmbeddableContact(),
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  hours: {
    type: HoursOfOperation,
    default: () => new HoursOfOperation(),
  },
  locationGroupIds: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});

const { id, hours, locationGroupIds, editable } = toRefs(props);
const { d, t } = useI18n();
const { isAuthenticatedUser } = useCurrentUserStore();
const { loadLocationGroups, formatLocationGroupNames } =
  useLocationGroupStore();

const locationGroupNames = computed(() =>
  formatLocationGroupNames(locationGroupIds.value),
);

onMounted(() => {
  loadLocationGroups({ includeVisible: true });
});

const isOnlyWeekdayHours = computed(() => {
  return (
    hours.value.hoursSameForAllDays &&
    hours.value.monday.open &&
    hours.value.tuesday.open &&
    hours.value.wednesday.open &&
    hours.value.thursday.open &&
    hours.value.friday.open &&
    !hours.value.saturday.open &&
    !hours.value.sunday.open
  );
});

const openHours = computed(() => {
  return hours.value.dailyHoursOfOperation.filter((day) => day.hours.open);
});

const formattedMondayTimes = computed(() => {
  if (!hours.value.monday.to || !hours.value.monday.from) {
    return '';
  }

  const fromTime = formatTime(hours.value.monday.from);
  const toTime = formatTime(hours.value.monday.to);
  return `${fromTime} - ${toTime}`;
});

const hoursOfOperationTooltip = computed(() => {
  if (isOnlyWeekdayHours) {
    const monday = t('hoursOfOperation.Monday');
    const friday = t('hoursOfOperation.Friday');

    return `${monday} - ${friday}\n${formattedMondayTimes.value}`;
  } else if (openHours.value.length) {
    return openHours.value
      .map((day) => {
        const fromTime = formatTime(day.hours.from);
        const toTime = formatTime(day.hours.to);
        return `${day.dayOfWeek.abbrev}\n${fromTime} - ${toTime}`;
      })
      .join('\n');
  } else {
    return undefined;
  }
});

const formatTime = (time: string) => {
  if (!time) {
    return '';
  }

  const isoDateString = DATE_STRING_TEMPLATE.replace('$time', time);
  return d(isoDateString, {
    hour: 'numeric',
    minute: '2-digit',
  });
};
</script>

<style>
.location-card-detail {
  .shipping-point-details {
    padding: var(--sp-6);
    width: 50%;

    .hours-of-operation {
      display: flex;
      flex-flow: column;
      gap: var(--sp-1);
      align-items: start;
    }

    .location-groups-list {
      white-space: normal;
      word-break: break-word;
    }
  }

  .contact-details {
    border-left: var(--divider);
    padding: var(--sp-6);
    width: 50%;
  }

  .popup-textinput.popup-inputgroup {
    .p-card {
      top: calc(var(--sp-4) * -1);
      left: 6px;
    }
  }

  .key-value-pair {
    .key-value-pair-label {
      font-size: 14px;
    }

    .key-value-pair-value {
      /* size of icon + gap, so the value lines up with the label not the icon */
      margin-left: calc(1rem + var(--sp-2));
    }
  }
}
</style>
